import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Braces, Image as ImageIcon, Plus, Trash2, Video } from "lucide-react";
import { AdminButton, AdminCard, AdminField, adminInputClass } from "@/components/admin/AdminUi";
import MediaPicker from "@/components/admin/MediaPicker";

const NEW_SECTION = {
  rich_text: { type: "rich_text", title: "New Section", body: "" },
  bullets: { type: "bullets", title: "Key Points", items: [{ title: "", text: "" }] },
  cta: {
    type: "cta",
    title: "Ready to take the next step?",
    text: "",
    primary: { label: "Learn More", href: "/contact" },
    secondary: { label: "", href: "" },
  },
  disclaimer: { type: "disclaimer", title: "Important", body: "" },
  media: {
    type: "media",
    title: "",
    media_type: "image",
    media_url: "",
    alt_text: "",
    caption: "",
  },
  team: {
    type: "team",
    title: "Board of Directors",
    text: "",
    members: [{ name: "", role: "", image_url: "", bio: "" }],
  },
  gallery: {
    type: "gallery",
    title: "Event Highlights",
    images: [{ image_url: "", caption: "" }],
  },
  honourees: {
    type: "honourees",
    title: "Past Honourees",
    honourees: [{ name: "", year: "", award: "", image_url: "", statement: "" }],
  },
};

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function TextInput({ label, value, onChange, placeholder = "" }) {
  return (
    <AdminField label={label}>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={adminInputClass}
        placeholder={placeholder}
      />
    </AdminField>
  );
}

function TextArea({ label, value, onChange, rows = 4, hint }) {
  return (
    <AdminField label={label} hint={hint}>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className={`${adminInputClass} leading-relaxed`}
      />
    </AdminField>
  );
}

function SectionEditor({
  section,
  index,
  total,
  onChange,
  onDelete,
  onMove,
  autoOpenMedia = false,
  onAutoOpenMediaHandled,
}) {
  const set = (key, value) => onChange({ ...section, [key]: value });

  const updateItem = (itemIndex, patch) => {
    const items = [...(section.items || [])];
    items[itemIndex] = { ...items[itemIndex], ...patch };
    set("items", items);
  };

  const updateMember = (memberIndex, patch) => {
    const members = [...(section.members || [])];
    members[memberIndex] = { ...members[memberIndex], ...patch };
    set("members", members);
  };

  return (
    <div id={`cms-page-section-anchor-${index}`}>
      <AdminCard className="space-y-5 border-slate-200 p-5" data-testid={`cms-page-section-${index}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-bka-emerald">Section {index + 1}</p>
          <h3 className="mt-1 font-display text-lg font-bold text-bka-navy">
            {section.type === "rich_text" ? "Text Section" :
             section.type === "bullets" ? "Cards / Key Points" :
             section.type === "cta" ? "Call to Action" :
             section.type === "disclaimer" ? "Notice / Disclaimer" :
             section.type === "media" ? "Image / Video" :
             section.type === "team" ? "Team / Board Members" :
             section.type === "gallery" ? "Photo Gallery" :
             section.type === "honourees" ? "Past Honourees" : "Content Section"}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onMove(index - 1)}
            disabled={index === 0}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 disabled:opacity-30"
            aria-label="Move section up"
          >
            <ArrowUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onMove(index + 1)}
            disabled={index === total - 1}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 disabled:opacity-30"
            aria-label="Move section down"
          >
            <ArrowDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
            aria-label="Delete section"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <TextInput label="Section Heading" value={section.title} onChange={(v) => set("title", v)} />

      {(section.type === "rich_text" || !section.type) && (
        <TextArea
          label="Content"
          value={section.body}
          onChange={(v) => set("body", v)}
          rows={8}
          hint="Write normal text. Separate paragraphs with a blank line. Use **words** if you want bold emphasis."
        />
      )}

      {section.type === "bullets" && (
        <div className="space-y-4">
          {(section.items || []).map((item, itemIndex) => (
            <div key={itemIndex} className="rounded-2xl bg-slate-50 p-4">
              <div className="grid gap-4 md:grid-cols-[1fr_1.8fr_auto]">
                <TextInput label={`Card ${itemIndex + 1} title`} value={item.title} onChange={(v) => updateItem(itemIndex, { title: v })} />
                <TextArea label="Description" value={item.text} onChange={(v) => updateItem(itemIndex, { text: v })} rows={3} />
                <div className="flex items-end pb-1">
                  <button
                    type="button"
                    onClick={() => set("items", (section.items || []).filter((_, i) => i !== itemIndex))}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-red-200 text-red-600"
                    aria-label={`Remove card ${itemIndex + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          <AdminButton
            type="button"
            onClick={() => set("items", [...(section.items || []), { title: "", text: "" }])}
          >
            <Plus className="h-4 w-4" /> Add Card
          </AdminButton>
          <TextArea label="Footnote (optional)" value={section.footnote} onChange={(v) => set("footnote", v)} rows={2} />
        </div>
      )}

      {section.type === "cta" && (
        <div className="space-y-4">
          <TextArea label="Supporting Text" value={section.text} onChange={(v) => set("text", v)} rows={3} />
          <div className="grid gap-4 md:grid-cols-2">
            <TextInput label="Primary Button Label" value={section.primary?.label} onChange={(v) => set("primary", { ...(section.primary || {}), label: v })} />
            <TextInput label="Primary Button Link" value={section.primary?.href} onChange={(v) => set("primary", { ...(section.primary || {}), href: v })} placeholder="/contact" />
            <TextInput label="Secondary Button Label" value={section.secondary?.label} onChange={(v) => set("secondary", { ...(section.secondary || {}), label: v })} />
            <TextInput label="Secondary Button Link" value={section.secondary?.href} onChange={(v) => set("secondary", { ...(section.secondary || {}), href: v })} placeholder="/get-support" />
          </div>
        </div>
      )}

      {section.type === "disclaimer" && (
        <TextArea label="Notice Text" value={section.body} onChange={(v) => set("body", v)} rows={5} />
      )}

      {section.type === "media" && (
        <div className="space-y-5">
          <AdminField label="What do you want to add?">
            <select
              value={section.media_type || "image"}
              onChange={(e) => {
                const nextType = e.target.value;
                onChange({ ...section, media_type: nextType, media_url: "" });
              }}
              className={adminInputClass}
            >
              <option value="image">Picture</option>
              <option value="video">Video</option>
            </select>
          </AdminField>

          {section.media_type === "video" ? (
            <div className="space-y-4">
              <MediaPicker
                kind="videos"
                label="Upload or Choose an MP4 Video"
                value={section.media_url}
                onChange={(url) => set("media_url", url)}
                buttonLabel="Choose or Upload Video"
                autoOpen={autoOpenMedia}
                onAutoOpenHandled={onAutoOpenMediaHandled}
              />
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-bold text-bka-navy">Or use a YouTube video</p>
                <p className="mb-2 mt-1 text-xs text-slate-500">Only use this box if the video is already on YouTube.</p>
                <input
                  value={String(section.media_url || "").startsWith("/api/media/") ? "" : section.media_url || ""}
                  onChange={(e) => set("media_url", e.target.value)}
                  className={adminInputClass}
                  placeholder="Paste the YouTube link here"
                />
              </div>
            </div>
          ) : (
            <MediaPicker
              kind="images"
              label="Picture for this section"
              value={section.media_url}
              onChange={(url) => set("media_url", url)}
              buttonLabel="Choose or Upload Picture"
              autoOpen={autoOpenMedia}
              onAutoOpenHandled={onAutoOpenMediaHandled}
            />
          )}

          <TextInput label="Picture description (optional)" value={section.alt_text} onChange={(v) => set("alt_text", v)} />
          <TextArea label="Caption (optional)" value={section.caption} onChange={(v) => set("caption", v)} rows={2} />
        </div>
      )}

      {section.type === "team" && (
        <div className="space-y-5">
          <TextArea label="Intro Text" value={section.text} onChange={(v) => set("text", v)} rows={3} />
          {(section.members || []).map((member, memberIndex) => (
            <div key={memberIndex} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-bold text-bka-navy">Board Member {memberIndex + 1}</p>
                <button
                  type="button"
                  onClick={() => set("members", (section.members || []).filter((_, i) => i !== memberIndex))}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600"
                  aria-label={`Remove board member ${memberIndex + 1}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <TextInput label="Name" value={member.name} onChange={(v) => updateMember(memberIndex, { name: v })} />
                <TextInput label="Position / Role" value={member.role} onChange={(v) => updateMember(memberIndex, { role: v })} />
                <div className="md:col-span-2">
                  <MediaPicker
                    kind="images"
                    label="Board Member Photo"
                    value={member.image_url}
                    onChange={(url) => updateMember(memberIndex, { image_url: url })}
                    buttonLabel="Choose or Upload Photo"
                  />
                </div>
                <div className="md:col-span-2">
                  <TextArea label="Short Bio (optional)" value={member.bio} onChange={(v) => updateMember(memberIndex, { bio: v })} rows={3} />
                </div>
              </div>
            </div>
          ))}
          <AdminButton
            type="button"
            onClick={() => set("members", [...(section.members || []), { name: "", role: "", image_url: "", bio: "" }])}
          >
            <Plus className="h-4 w-4" /> Add Board Member
          </AdminButton>
        </div>
      )}

      {section.type === "gallery" && (
        <div className="space-y-5">
          {(section.images || []).map((img, imgIndex) => (
            <div key={imgIndex} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-bold text-bka-navy">Photograph {imgIndex + 1}</p>
                <button
                  type="button"
                  onClick={() => set("images", (section.images || []).filter((_, i) => i !== imgIndex))}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600"
                  aria-label={`Remove photograph ${imgIndex + 1}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <MediaPicker
                kind="images"
                label="Photograph"
                value={img.image_url}
                onChange={(url) => {
                  const images = [...(section.images || [])];
                  images[imgIndex] = { ...images[imgIndex], image_url: url };
                  set("images", images);
                }}
                buttonLabel="Choose or Upload Photograph"
                autoOpen={autoOpenMedia && imgIndex === 0}
                onAutoOpenHandled={onAutoOpenMediaHandled}
              />
              <div className="mt-4">
                <TextInput
                  label="Caption (optional)"
                  value={img.caption}
                  onChange={(v) => {
                    const images = [...(section.images || [])];
                    images[imgIndex] = { ...images[imgIndex], caption: v };
                    set("images", images);
                  }}
                />
              </div>
            </div>
          ))}
          <AdminButton
            type="button"
            onClick={() => set("images", [...(section.images || []), { image_url: "", caption: "" }])}
          >
            <Plus className="h-4 w-4" /> Add Photograph
          </AdminButton>
        </div>
      )}

      {section.type === "honourees" && (
        <div className="space-y-5">
          {(section.honourees || []).map((h, hIndex) => {
            const patch = (key) => (v) => {
              const list = [...(section.honourees || [])];
              list[hIndex] = { ...list[hIndex], [key]: v };
              set("honourees", list);
            };
            return (
              <div key={hIndex} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-sm font-bold text-bka-navy">Honouree {hIndex + 1}</p>
                  <button
                    type="button"
                    onClick={() => set("honourees", (section.honourees || []).filter((_, i) => i !== hIndex))}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600"
                    aria-label={`Remove honouree ${hIndex + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <TextInput label="Name" value={h.name} onChange={patch("name")} />
                  <TextInput label="Year" value={h.year} onChange={patch("year")} placeholder="2026" />
                  <TextInput label="Award Received" value={h.award} onChange={patch("award")} />
                  <div className="md:col-span-3">
                    <MediaPicker
                      kind="images"
                      label="Honouree Photograph"
                      value={h.image_url}
                      onChange={patch("image_url")}
                      buttonLabel="Choose or Upload Photograph"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <TextArea
                      label="Recognition Statement (optional)"
                      value={h.statement}
                      rows={3}
                      onChange={patch("statement")}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          <AdminButton
            type="button"
            onClick={() =>
              set("honourees", [
                ...(section.honourees || []),
                { name: "", year: "", award: "", image_url: "", statement: "" },
              ])
            }
          >
            <Plus className="h-4 w-4" /> Add Honouree
          </AdminButton>
        </div>
      )}

      </AdminCard>
    </div>
  );
}

export default function PageVisualEditor({ value, onChange }) {
  const safeValue = useMemo(
    () => value || { hero: { overline: "", heading_line_1: "", heading_line_2: "", subheading: "" }, sections: [] },
    [value]
  );
  const [newType, setNewType] = useState("rich_text");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [raw, setRaw] = useState(() => JSON.stringify(safeValue, null, 2));
  const [rawError, setRawError] = useState("");
  const [autoOpenMediaIndex, setAutoOpenMediaIndex] = useState(null);

  const update = (next) => {
    onChange(next);
    setRaw(JSON.stringify(next, null, 2));
    setRawError("");
  };

  const updateHero = (key, val) => update({ ...safeValue, hero: { ...(safeValue.hero || {}), [key]: val } });
  const sections = safeValue.sections || [];

  const updateSection = (index, nextSection) => {
    const next = [...sections];
    next[index] = nextSection;
    update({ ...safeValue, sections: next });
  };

  const deleteSection = (index) => update({ ...safeValue, sections: sections.filter((_, i) => i !== index) });

  const moveSection = (from, to) => {
    if (to < 0 || to >= sections.length) return;
    const next = [...sections];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    update({ ...safeValue, sections: next });
  };

  const addSection = (type = newType) => {
    const nextIndex = sections.length;
    update({ ...safeValue, sections: [...sections, clone(NEW_SECTION[type])] });

    if (type === "media" || type === "gallery") {
      setAutoOpenMediaIndex(nextIndex);
      window.setTimeout(() => {
        document
          .getElementById(`cms-page-section-anchor-${nextIndex}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
    }
  };

  return (
    <div className="space-y-6" data-testid="cms-pages-visual-editor">
      <AdminCard className="space-y-5 border-emerald-100 bg-emerald-50/30 p-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-bka-emerald">Page Banner</p>
          <p className="mt-1 text-sm text-slate-600">Edit what visitors see at the top of this page. No code is required.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <TextInput label="Small Heading" value={safeValue.hero?.overline} onChange={(v) => updateHero("overline", v)} placeholder="About Us" />
          <TextInput label="Main Heading — Line 1" value={safeValue.hero?.heading_line_1} onChange={(v) => updateHero("heading_line_1", v)} />
          <TextInput label="Main Heading — Line 2" value={safeValue.hero?.heading_line_2} onChange={(v) => updateHero("heading_line_2", v)} />
          <div className="md:col-span-2">
            <TextArea label="Intro / Subheading" value={safeValue.hero?.subheading} onChange={(v) => updateHero("subheading", v)} rows={3} />
          </div>
        </div>
      </AdminCard>

      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-bold text-bka-navy">Page Content</h2>
            <p className="mt-1 text-sm text-slate-600">Click Add Picture once. The picture chooser opens immediately — no URLs or copy/paste.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <AdminButton type="button" onClick={() => addSection("rich_text")}>
              <Plus className="h-4 w-4" /> Add Text
            </AdminButton>
            <AdminButton type="button" variant="emerald" onClick={() => addSection("media")}>
              <ImageIcon className="h-4 w-4" /> Add Picture
            </AdminButton>
            <AdminButton type="button" onClick={() => addSection("bullets")}>
              <Plus className="h-4 w-4" /> Add Cards
            </AdminButton>
            <AdminButton type="button" onClick={() => addSection("team")}>
              <Plus className="h-4 w-4" /> Add Board / Team
            </AdminButton>
            <select value={newType} onChange={(e) => setNewType(e.target.value)} className={`${adminInputClass} w-auto min-w-[165px]`}>
              <option value="cta">More: Button / CTA</option>
              <option value="disclaimer">More: Notice</option>
              <option value="media">More: Picture / Video</option>
              <option value="gallery">More: Photo Gallery</option>
              <option value="honourees">More: Past Honourees</option>
            </select>
            <AdminButton type="button" variant="ghost" onClick={() => addSection(newType)}>
              <Plus className="h-4 w-4" /> Add
            </AdminButton>
          </div>
        </div>

        {sections.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-600">
            This page has no content sections yet. Choose a section type above and click Add Section.
          </div>
        ) : (
          sections.map((section, index) => (
            <SectionEditor
              key={`${section.type || "section"}-${index}`}
              section={section}
              index={index}
              total={sections.length}
              onChange={(next) => updateSection(index, next)}
              onDelete={() => deleteSection(index)}
              onMove={(to) => moveSection(index, to)}
              autoOpenMedia={
                (section.type === "media" || section.type === "gallery") &&
                autoOpenMediaIndex === index
              }
              onAutoOpenMediaHandled={() => setAutoOpenMediaIndex(null)}
            />
          ))
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <button
          type="button"
          onClick={() => {
            setShowAdvanced((v) => !v);
            setRaw(JSON.stringify(safeValue, null, 2));
            setRawError("");
          }}
          className="flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-bka-navy"
        >
          <Braces className="h-4 w-4" /> Advanced JSON {showAdvanced ? "(hide)" : "(developers only)"}
        </button>
        {showAdvanced && (
          <div className="mt-4">
            <textarea
              value={raw}
              onChange={(e) => {
                const nextRaw = e.target.value;
                setRaw(nextRaw);
                try {
                  const parsed = JSON.parse(nextRaw);
                  onChange(parsed);
                  setRawError("");
                } catch {
                  setRawError("JSON is not valid yet. The visual editor has not been changed.");
                }
              }}
              rows={18}
              className={`${adminInputClass} font-mono text-[12px] leading-relaxed`}
            />
            {rawError && <p className="mt-2 text-sm font-semibold text-red-600">{rawError}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
